// src/components/NotificationSettingsView.tsx
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { UserProfile, ToastType, NotificationSettings } from "../types";
import { getRoddoUiTokens, resolveRoddoDarkMode } from "../constants/theme";

interface NotificationSettingsViewProps {
  userProfile: UserProfile;
  onUpdateProfile: (updated: UserProfile) => void | Promise<void>;
  onBack: () => void;
  addToast: (message: string, type?: ToastType) => void;
  onOpenSellerDashboard?: () => void;
  isDarkMode?: boolean;
}

type NotificationSettingsForm = NotificationSettings & {
  shareCrewMapPresence?: boolean;
  crewActivity?: boolean;
  crewRequests?: boolean;
};

type AccentKey = "purple" | "blue" | "green" | "gold" | "muted";

const DEFAULTS: NotificationSettingsForm = {
  enabled: true,
  newLikes: true,
  newComments: true,
  priceDrops: false,
  showPostCatchInsight: true,
  shareCrewMapPresence: false,
  crewActivity: true,
  crewRequests: true,
};

function accentStyle(accent: AccentKey, enabled: boolean, isDarkMode: boolean) {
  const inactive = {
    bg: isDarkMode ? '#0C1423' : '#F2EBE1',
    border: isDarkMode ? 'rgba(180,195,220,0.14)' : 'rgba(74,58,42,0.14)',
    glow: 'none',
  };

  if (!enabled || accent === "muted") return inactive;

  // Brand action color stays consistent across settings. Semantic accents are
  // reserved for settings whose meaning is actually success/reward related.
  if (accent === "green") {
    return {
      bg: isDarkMode ? '#2B7A63' : '#49B98E',
      border: isDarkMode ? 'rgba(122,232,184,0.28)' : 'rgba(36,137,101,0.24)',
      glow: 'none',
    };
  }

  if (accent === "gold") {
    return {
      bg: isDarkMode ? '#9B7628' : '#D7A82E',
      border: isDarkMode ? 'rgba(246,196,83,0.28)' : 'rgba(153,107,12,0.22)',
      glow: 'none',
    };
  }

  return {
    bg: isDarkMode ? '#8A70F2' : '#2F80ED',
    border: isDarkMode ? 'rgba(185,172,255,0.28)' : 'rgba(47,128,237,0.24)',
    glow: 'none',
  };
}

function RoddoToggle({
  checked,
  disabled,
  accent,
  isDarkMode,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  accent: AccentKey;
  isDarkMode: boolean;
  onChange: (next: boolean) => void;
}) {
  const tone = accentStyle(accent, checked, isDarkMode);

  return (
    <button
      type="button"
      aria-pressed={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="relative h-7 w-[52px] shrink-0 rounded-full border p-[3px] transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50"
      style={{
        background: tone.bg,
        borderColor: tone.border,
        boxShadow: tone.glow,
      }}
    >
      <span
        className="block h-5 w-5 rounded-full bg-white shadow-[0_2px_8px_rgba(0,0,0,0.22)] transition-transform duration-200 ease-out"
        style={{ transform: checked ? "translateX(24px)" : "translateX(0)" }}
      />
    </button>
  );
}

function SettingRow({
  title,
  body,
  value,
  disabled,
  accent,
  isDarkMode,
  onChange,
}: {
  title: string;
  body?: string;
  value: boolean;
  disabled?: boolean;
  accent: AccentKey;
  isDarkMode: boolean;
  onChange: (next: boolean) => void;
}) {
  const ui = getRoddoUiTokens(isDarkMode);

  return (
    <div
      className="flex items-center justify-between gap-4 rounded-[14px] border px-4 py-3 transition-colors duration-200"
      style={{
        background: ui.cardBgMuted,
        borderColor: ui.border,
        boxShadow: isDarkMode ? "none" : "0 4px 12px rgba(89,65,37,0.04)",
        opacity: disabled ? 0.72 : 1,
      }}
    >
      <div className="min-w-0 flex-1">
        <div
          className="text-sm font-black leading-tight"
          style={{ color: ui.textPrimary }}
        >
          {title}
        </div>
        {body ? (
          <p
            className="mt-1 max-w-[360px] text-[11px] font-semibold leading-4"
            style={{ color: ui.textSecondary }}
          >
            {body}
          </p>
        ) : null}
      </div>

      <RoddoToggle
        checked={!!value}
        disabled={disabled}
        accent={accent}
        isDarkMode={isDarkMode}
        onChange={onChange}
      />
    </div>
  );
}

function SettingsSection({
  title,
  body,
  children,
  isDarkMode,
  headerAction,
}: {
  title: string;
  body?: string;
  children: React.ReactNode;
  isDarkMode: boolean;
  headerAction?: React.ReactNode;
}) {
  const ui = getRoddoUiTokens(isDarkMode);

  return (
    <section
      className="rounded-[18px] border p-4 transition-colors duration-200"
      style={{
        background: ui.cardBg,
        borderColor: ui.borderStrong ?? ui.border,
        boxShadow: isDarkMode ? "none" : "0 6px 16px rgba(89,65,37,0.05)",
      }}
    >
      <div className="mb-3 flex items-start justify-between gap-3 px-1">
        <div className="min-w-0 flex-1">
          <div
            className="text-[10px] font-black uppercase tracking-[0.18em]"
            style={{ color: ui.textMuted }}
          >
            {title}
          </div>
          {body ? (
            <p
              className="mt-1 text-[12px] font-semibold leading-5"
              style={{ color: ui.textSecondary }}
            >
              {body}
            </p>
          ) : null}
        </div>
        {headerAction ? <div className="shrink-0">{headerAction}</div> : null}
      </div>

      <div className="space-y-2.5">{children}</div>
    </section>
  );
}

const NotificationSettingsView: React.FC<NotificationSettingsViewProps> = ({
  userProfile,
  onUpdateProfile,
  onBack,
  addToast,
  isDarkMode: explicitIsDarkMode,
}) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const isDarkMode = resolveRoddoDarkMode(explicitIsDarkMode);
  const ui = getRoddoUiTokens(isDarkMode);

  const initial = useMemo<NotificationSettingsForm>(() => {
    const s = userProfile.notificationSettings as
      | NotificationSettingsForm
      | undefined;
    return {
      enabled: s?.enabled ?? DEFAULTS.enabled,
      newLikes: s?.newLikes ?? DEFAULTS.newLikes,
      newComments: s?.newComments ?? DEFAULTS.newComments,
      priceDrops: s?.priceDrops ?? DEFAULTS.priceDrops,
      showPostCatchInsight:
        s?.showPostCatchInsight ?? DEFAULTS.showPostCatchInsight,
      shareCrewMapPresence:
        s?.shareCrewMapPresence ?? DEFAULTS.shareCrewMapPresence,
      crewActivity:
        (s as any)?.crewActivity ?? DEFAULTS.crewActivity,
      crewRequests:
        (s as any)?.crewRequests ?? DEFAULTS.crewRequests,
    };
  }, [userProfile.notificationSettings]);

  const [settings, setSettings] = useState<NotificationSettingsForm>(initial);
  const masterEnabled = settings.enabled ?? true;

  const handleSave = async () => {
    try {
      setSaving(true);

      const updated: UserProfile = {
        ...userProfile,
        notificationSettings: settings,
      };

      await onUpdateProfile(updated);
      addToast(t("notifications.settingsSaved", "Settings saved."), "success");
    } catch (err) {
      console.error("Failed to save settings", err);
      addToast(t("common.errorGeneric", "Something went wrong."), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="roddo-theme-lock mx-auto max-w-2xl space-y-3.5 px-3 pb-6 pt-3 sm:pt-4"
      data-roddo-theme={isDarkMode ? "dark" : "light"}
      style={{ background: "transparent", color: ui.textPrimary }}
    >
      <section
        className="relative overflow-hidden rounded-[18px] border p-4 transition-colors duration-200"
        style={{
          background: isDarkMode ? "#101827" : "#FFFCF7",
          borderColor: ui.borderStrong ?? ui.border,
          boxShadow: isDarkMode ? "none" : "0 6px 16px rgba(89,65,37,0.05)",
        }}
      >
        <div className="absolute right-4 top-4 z-[3]">
          <button
            type="button"
            onClick={onBack}
            className="grid h-10 w-10 place-items-center rounded-full border text-xl font-black transition-transform hover:-translate-y-0.5 active:translate-y-[1px]"
            style={{
              background: ui.cardBgMuted,
              borderColor: ui.border,
              color: ui.textPrimary,
              boxShadow: isDarkMode ? "none" : "0 4px 12px rgba(89,65,37,0.04)",
            }}
            aria-label="Go back"
            title="Back"
          >
            ‹
          </button>
        </div>

        <div className="relative pr-14">
          <div
            className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em]"
            style={{
              background: ui.cardBgMuted,
              borderColor: ui.border,
              color: ui.textMuted,
            }}
          >
            Roddo
            <span aria-hidden="true">⚙️</span>
          </div>
          <h1
            className="mt-3 text-3xl font-black tracking-tight"
            style={{ color: ui.textPrimary }}
          >
            Settings
          </h1>
          <p
            className="mt-2 max-w-md text-sm font-semibold leading-6"
            style={{ color: ui.textSecondary }}
          >
            Notifications, fishing insights, crew map privacy, and account
            controls.
          </p>
        </div>
      </section>

      <SettingsSection
        title="Master"
        body="One global switch for notifications."
        isDarkMode={isDarkMode}
      >
        <SettingRow
          title={t("notifications.enabled", "Enable notifications")}
          body={t(
            "notifications.enabledDesc",
            "Master switch for all Roddo notifications.",
          )}
          value={masterEnabled}
          onChange={(v) => setSettings((s) => ({ ...s, enabled: v }))}
          disabled={saving}
          accent="blue"
          isDarkMode={isDarkMode}
        />
      </SettingsSection>

      <SettingsSection
        title="Community"
        body="Baleen Bay activity from posts and comments."
        isDarkMode={isDarkMode}
      >
        <SettingRow
          title={t("notifications.newLikes", "Likes")}
          body={t(
            "notifications.newLikesDesc",
            "Get notified when someone likes your post.",
          )}
          value={settings.newLikes ?? true}
          onChange={(v) => setSettings((s) => ({ ...s, newLikes: v }))}
          disabled={saving || !masterEnabled}
          accent="blue"
          isDarkMode={isDarkMode}
        />

        <SettingRow
          title={t("notifications.newComments", "Comments")}
          body={t(
            "notifications.newCommentsDesc",
            "Get notified when someone comments on your post.",
          )}
          value={settings.newComments ?? true}
          onChange={(v) => setSettings((s) => ({ ...s, newComments: v }))}
          disabled={saving || !masterEnabled}
          accent="blue"
          isDarkMode={isDarkMode}
        />
      </SettingsSection>

      <SettingsSection
        title="Crew"
        body="Private crew activity and connection updates."
        isDarkMode={isDarkMode}
      >
        <SettingRow
          title="Crew activity"
          body="New posts and replies from your private crews."
          value={settings.crewActivity ?? true}
          onChange={(v) => setSettings((s) => ({ ...s, crewActivity: v }))}
          disabled={saving || !masterEnabled}
          accent="blue"
          isDarkMode={isDarkMode}
        />

        <SettingRow
          title="Crew requests"
          body="Crew passes, accepts, and connection updates."
          value={settings.crewRequests ?? true}
          onChange={(v) => setSettings((s) => ({ ...s, crewRequests: v }))}
          disabled={saving || !masterEnabled}
          accent="green"
          isDarkMode={isDarkMode}
        />
      </SettingsSection>

      <SettingsSection
        title="Fishing"
        body="Helpful Roddo moments after real catch logging."
        isDarkMode={isDarkMode}
      >
        <SettingRow
          title={t(
            "notifications.showPostCatchInsight",
            "Show Insight after catch",
          )}
          body={t(
            "notifications.showPostCatchInsightDesc",
            "Auto-open the Roddo Insight card after saving a catch.",
          )}
          value={settings.showPostCatchInsight ?? true}
          onChange={(v) =>
            setSettings((s) => ({ ...s, showPostCatchInsight: v }))
          }
          disabled={saving}
          accent="gold"
          isDarkMode={isDarkMode}
        />
      </SettingsSection>

      <SettingsSection
        title="Privacy"
        body="Crew map presence is approximate and accepted-crew only."
        isDarkMode={isDarkMode}
      >
        <SettingRow
          title={t("notifications.shareCrewMapPresence", "Crew map presence")}
          body={t(
            "notifications.shareCrewMapPresenceDesc",
            "Show your approximate app-open location to accepted Crew on Big Blue.",
          )}
          value={settings.shareCrewMapPresence ?? false}
          onChange={(v) =>
            setSettings((s) => ({ ...s, shareCrewMapPresence: v }))
          }
          disabled={saving}
          accent="green"
          isDarkMode={isDarkMode}
        />
      </SettingsSection>

      <SettingsSection
        title="Bazaar"
        body="Marketplace tools stay quiet while Bazaar remains gated."
        isDarkMode={isDarkMode}
      >
        <SettingRow
          title={t("notifications.priceDrops", "Marketplace updates")}
          body={t(
            "notifications.priceDropsDesc",
            "Price drops, offers, and purchase updates. Hidden until Bazaar returns.",
          )}
          value={settings.priceDrops ?? false}
          onChange={(v) => setSettings((s) => ({ ...s, priceDrops: v }))}
          disabled
          accent="muted"
          isDarkMode={isDarkMode}
        />
      </SettingsSection>

      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        className="w-full rounded-[14px] px-4 py-3 text-sm font-black transition-transform hover:-translate-y-0.5 active:translate-y-[1px] disabled:cursor-not-allowed disabled:opacity-60"
        style={{
          background: isDarkMode ? "#8A70F2" : "#2F80ED",
          color: "#FFFFFF",
          border: `1px solid ${isDarkMode ? "rgba(185,172,255,.24)" : "rgba(47,128,237,.24)"}`,
          boxShadow: isDarkMode ? "none" : "0 8px 18px rgba(47,128,237,.16)",
        }}
      >
        {saving
          ? t("common.saving", "Saving…")
          : t("common.save", "Save settings")}
      </button>
    </div>
  );
};

export default NotificationSettingsView;
